import { useState, useEffect } from 'react';
import { api, apiPost } from '../api';
import { useI18n } from '../i18n/context';
import '../styles/ui.css';
import './ListPages.css';

export default function Security() {
  const { t } = useI18n();
  const [status, setStatus] = useState({ enabled: false });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [setup, setSetup] = useState(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);

  useEffect(() => {
    api('/superadmin/auth/me')
      .then((data) => setUser(data.user || null))
      .catch(() => setUser(null));
    api('/superadmin/auth/2fa/status')
      .then(setStatus)
      .catch(() => setStatus({ enabled: false }))
      .finally(() => setLoading(false));
  }, []);

  async function handleStartSetup() {
    setError('');
    setSaving(true);
    try {
      const data = await apiPost('/superadmin/auth/2fa/setup', {});
      setSetup({ secret: data.secret || data.manualEntry, qrDataUrl: data.qrDataUrl });
      setConfirmCode('');
    } catch (e) {
      setError(e.message || t('toast.error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmSetup() {
    if (!setup || !confirmCode.trim() || confirmCode.trim().length < 6) {
      setError(t('twoFa.enterCode'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      const data = await apiPost('/superadmin/auth/2fa/confirm', {
        code: confirmCode.trim(),
        secret: setup.secret
      });
      setRecoveryCodes(data.recoveryCodes || []);
      setSetup(null);
      setStatus({ enabled: true });
    } catch (e) {
      setError(e.message || t('toast.error'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDisable() {
    if (!disablePassword || !disableCode.trim()) {
      setError(t('twoFa.disablePassword') + ' / ' + t('twoFa.disableCode'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      await apiPost('/superadmin/auth/2fa/disable', {
        password: disablePassword,
        code: disableCode.trim()
      });
      setStatus({ enabled: false });
      setDisablePassword('');
      setDisableCode('');
      setDisableModalOpen(false);
    } catch (e) {
      setError(e.message || t('toast.error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="sa-page-frame">
        <div className="sa-page-header">
          <h1 className="sa-page-title">{t('twoFa.title')}</h1>
          <p className="sa-page-subtitle">{t('twoFa.subtitle')}</p>
        </div>
        <div className="ui-page-content">
          <div className="sa-loading" style={{ padding: 16 }}>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('twoFa.title')}</h1>
        <p className="sa-page-subtitle">{t('twoFa.subtitle')}</p>
      </div>

      <div className="ui-page-content">
        {error && (
          <div className="sa-toast sa-toast--error" style={{ position: 'relative', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {recoveryCodes && recoveryCodes.length > 0 && (
          <div className="sa-card" style={{ marginBottom: '1.5rem' }}>
            <h2 className="sa-modal-title">{t('twoFa.recoveryCodes')}</h2>
            <p className="sa-modal-text">{t('twoFa.recoveryCodesDesc')}</p>
            <pre style={{ background: '#f1f5f9', padding: '1rem', borderRadius: 8, overflow: 'auto' }}>
              {recoveryCodes.join('\n')}
            </pre>
            <button type="button" className="sa-btn-primary" onClick={() => setRecoveryCodes(null)}>
              {t('actions.close')}
            </button>
          </div>
        )}

        <div className="sa-security-grid">
          <div className="sa-card sa-security-card">
            <div className="sa-card-header">
              <h2 className="sa-card-title">{t('security.overviewTitle')}</h2>
            </div>
            <div className="sa-security-overview">
              <div className="sa-security-row">
                <span className="sa-security-label">{t('security.accountEmail')}</span>
                <span className="sa-security-value">{user?.email || '—'}</span>
              </div>
              <div className="sa-security-row">
                <span className="sa-security-label">{t('security.twoFactor')}</span>
                <span className={`sa-badge ${status.enabled ? 'sa-badge--success' : 'sa-badge--error'}`}>
                  {status.enabled ? t('security.enabled') : t('security.disabled')}
                </span>
              </div>
              <div className="sa-security-row">
                <span className="sa-security-label">{t('security.lastLogin')}</span>
                <span className="sa-security-value">—</span>
              </div>
              <div className="sa-security-row">
                <span className="sa-security-label">{t('security.lastLoginIp')}</span>
                <span className="sa-security-value">—</span>
              </div>
              <div className="sa-security-row">
                <span className="sa-security-label">{t('security.authApp')}</span>
                <span className="sa-security-value">—</span>
              </div>
              <div className="sa-security-row">
                <span className="sa-security-label">{t('security.recoveryCodes')}</span>
                <span className="sa-security-value">
                  {recoveryCodes && recoveryCodes.length > 0 ? t('security.available') : t('security.notGenerated')}
                </span>
              </div>
            </div>
          </div>

        <div className="sa-card sa-security-card">
          <div className="sa-card-header">
            <h2 className="sa-card-title">{t('twoFa.title')}</h2>
            <span className={`sa-badge ${status.enabled ? 'sa-badge--success' : 'sa-badge--error'}`}>
              {status.enabled ? t('security.enabled') : t('security.disabled')}
            </span>
          </div>
          <p className="sa-text-muted">{t('twoFa.subtitle')}</p>

          {!status.enabled && !setup && (
            <button type="button" className="sa-btn-primary" onClick={handleStartSetup} disabled={saving}>
              {t('twoFa.enable')}
            </button>
          )}

          {setup && (
            <div className="sa-security-setup">
              <p className="sa-modal-text">{t('twoFa.scanQr')}</p>
              {setup.qrDataUrl && (
                <img src={setup.qrDataUrl} alt="QR" className="sa-security-qr" />
              )}
              <p className="sa-modal-text">
                <strong>{t('twoFa.manualCode')}:</strong> <code>{setup.secret}</code>
              </p>
              <div className="sa-form-group">
                <label className="sa-label">{t('twoFa.enterCode')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="sa-input"
                  value={confirmCode}
                  onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="000000"
                />
              </div>
              <div className="sa-modal-actions">
                <button type="button" className="sa-btn-secondary" onClick={() => setSetup(null)}>
                  {t('actions.cancel')}
                </button>
                <button
                  type="button"
                  className="sa-btn-primary"
                  onClick={handleConfirmSetup}
                  disabled={saving || confirmCode.length < 6}
                >
                  {saving ? t('common.loading') : t('twoFa.confirm')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="sa-card sa-security-card">
          <div className="sa-card-header">
            <h2 className="sa-card-title">{t('security.recoveryTitle')}</h2>
          </div>
          <p className="sa-text-muted">{t('security.recoveryDesc')}</p>
          <div className="sa-security-actions">
            <button type="button" className="sa-btn-secondary" disabled>
              {t('security.viewRecovery')}
            </button>
            <button type="button" className="sa-btn-secondary" disabled>
              {t('security.generateRecovery')}
            </button>
          </div>
        </div>

        <div className="sa-card sa-security-card sa-danger-zone">
          <div className="sa-card-header">
            <h2 className="sa-card-title">{t('security.dangerTitle')}</h2>
          </div>
          <p className="sa-text-muted">{t('security.dangerDesc')}</p>
          <div className="sa-form-group">
            <label className="sa-label">{t('twoFa.disablePassword')}</label>
            <input
              type="password"
              className="sa-input"
              value={disablePassword}
              onChange={(e) => setDisablePassword(e.target.value)}
            />
          </div>
          <div className="sa-form-group">
            <label className="sa-label">{t('twoFa.disableCode')}</label>
            <input
              type="text"
              className="sa-input"
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              placeholder="000000"
            />
          </div>
          <button
            type="button"
            className="sa-btn-danger"
            onClick={() => setDisableModalOpen(true)}
            disabled={saving || !disablePassword || !disableCode.trim()}
          >
            {saving ? t('common.loading') : t('twoFa.disableConfirm')}
          </button>
        </div>
      </div>

      {disableModalOpen && (
        <div className="sa-modal-overlay" onClick={() => !saving && setDisableModalOpen(false)}>
          <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="sa-modal-title">{t('security.disableModalTitle')}</h2>
            <p className="sa-modal-text">{t('security.disableModalText')}</p>
            <div className="sa-modal-actions">
              <button type="button" className="sa-btn-secondary" onClick={() => setDisableModalOpen(false)} disabled={saving}>
                {t('actions.cancel')}
              </button>
              <button type="button" className="sa-btn-danger" onClick={handleDisable} disabled={saving}>
                {saving ? t('common.loading') : t('security.disableConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
  );
}
